import React, { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw, FileText, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin → Waiver Settings.
 * Edit the master liability-waiver text. Optionally bump the version on save —
 * which triggers the "Please re-sign" banner for every active member.
 */
const AdminWaiverSettings = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ version: "", text: "", updated_at: null, updated_by: null });
  const [draft, setDraft] = useState("");
  const [bumpVersion, setBumpVersion] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/waiver-text", { withCredentials: true });
      setData(data);
      setDraft(data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load waiver");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    if (draft.trim().length < 50) {
      toast.error("Waiver text seems too short.");
      return;
    }
    setSaving(true);
    try {
      const { data: saved } = await api.put(
        "/admin/waiver-text",
        { text: draft.trim(), bump_version: bumpVersion },
        { withCredentials: true }
      );
      setData(saved);
      setDraft(saved.text);
      setBumpVersion(false);
      if (saved.bumped) {
        toast.success("Saved — existing members will see a re-sign banner.");
      } else {
        toast.success("Waiver text saved.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetDraft = () => {
    setDraft(data.text);
    setBumpVersion(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-spike-blue" />
      </div>
    );
  }

  const dirty = draft.trim() !== (data.text || "").trim();

  return (
    <div className="space-y-4" data-testid="admin-waiver-settings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-orbitron font-bold text-spike-text uppercase tracking-wide">
            Waiver text
          </h2>
          <p className="text-sm text-spike-text-muted mt-1 font-exo">
            The liability waiver shown on <code className="text-spike-blue-light">/checkin</code> and embedded
            in the generated PDFs. Snapshotted per-signature — old waivers always render the text the user actually saw.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <FileText className="w-4 h-4 text-spike-blue-light" />
          <span className="text-xs font-orbitron uppercase tracking-[0.18em] text-spike-text-muted">
            Versioned automatically
          </span>
        </div>
      </div>

      {data.updated_at ? (
        <div className="text-xs text-spike-dim font-exo">
          Last updated {new Date(data.updated_at).toLocaleString()} by <code className="text-spike-blue-light">{data.updated_by}</code>
        </div>
      ) : null}

      <div className="bg-spike-card border border-white/10 rounded-lg p-5 space-y-4">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="admin-waiver-text-textarea"
          rows={22}
          spellCheck={false}
          className="w-full bg-black/60 border border-white/15 rounded-md px-4 py-3 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue font-mono leading-relaxed"
        />

        <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-md bg-amber-500/5 border border-amber-500/20" data-testid="admin-waiver-bump-label">
          <input
            type="checkbox"
            checked={bumpVersion}
            onChange={(e) => setBumpVersion(e.target.checked)}
            data-testid="admin-waiver-bump-checkbox"
            className="mt-0.5 w-4 h-4 accent-amber-400 cursor-pointer flex-shrink-0"
          />
          <span className="text-sm text-spike-text font-exo leading-relaxed">
            <strong className="text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Bump the version on save
            </strong>
            <span className="block text-spike-text-muted text-xs mt-1">
              All members who signed earlier versions will see a "Waiver updated — please re-sign before your next visit" banner.
              Tick this only when the legal terms have changed (not for typo fixes).
            </span>
          </span>
        </label>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="admin-waiver-save-btn"
            className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 disabled:cursor-not-allowed text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {bumpVersion ? `Save & bump to next version` : `Save changes`}
          </button>
          <button
            type="button"
            onClick={resetDraft}
            disabled={!dirty}
            data-testid="admin-waiver-discard-btn"
            className="inline-flex items-center gap-2 text-xs font-orbitron uppercase tracking-[0.15em] text-spike-text-muted hover:text-spike-text disabled:opacity-40 disabled:cursor-not-allowed border border-white/15 hover:border-white/30 px-3 py-2 rounded-sm"
          >
            <RotateCcw className="w-3 h-3" /> Discard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminWaiverSettings;
